import { CommonServerSideParams } from '@/app/types/CommonServerSideParams';
import Loader from '@/common/components/animations/Loader';
import { OnlyBrowserPageProps } from '@/layouts/core/types/OnlyBrowserPageProps';
import { SSGPageProps } from '@/layouts/core/types/SSGPageProps';
import DefaultLayout from '@/layouts/public/components/PublicLayout';
import {
  getPublicLayoutStaticPaths,
  getPublicLayoutStaticProps,
} from '@/layouts/public/publicLayoutSSG';
import { AMPLITUDE_PAGES } from '@/modules/core/amplitude/events';
import useAmplitude from '@/modules/core/amplitude/hooks/useAmplitude';
import useCustomer from '@/modules/core/data/hooks/useCustomer';
import { AirtableRecord } from '@/modules/core/data/types/AirtableRecord';
import { Campus } from '@/modules/core/data/types/Campus';
import { Customer } from '@/modules/core/data/types/Customer';
import { EducationalProgram } from '@/modules/core/data/types/EducationalProgram';
import { StudentSolution } from '@/modules/core/data/types/StudentSolution';
import useI18n, { I18n } from '@/modules/core/i18n/hooks/useI18n';
import { resolveSimulatorSettings } from '@/modules/simulator/SLS';
import { Amplitude } from '@amplitude/react-amplitude';
import {
  css,
  useTheme,
} from '@emotion/react';
import { isBrowser } from '@unly/utils';
import { createLogger } from '@/modules/core/logging/logger';
import { Identify } from 'amplitude-js';
import {
  GetStaticPaths,
  GetStaticProps,
  NextPage,
} from 'next';
// eslint-disable-next-line @typescript-eslint/no-unused-vars,no-unused-vars
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

const fileLabel = 'pages/[locale]/simulator';
const logger = createLogger({ // eslint-disable-line no-unused-vars,@typescript-eslint/no-unused-vars
  fileLabel,
});

/**
 * Only executed on the server side at build time
 * Necessary when a page has dynamic routes and uses "getStaticProps"
 */
export const getStaticPaths: GetStaticPaths<CommonServerSideParams> = getPublicLayoutStaticPaths;

/**
 * Only executed on the server side at build time.
 *
 * @return Props (as "SSGPageProps") that will be passed to the Page component, as props
 *
 * @see https://github.com/vercel/next.js/discussions/10949#discussioncomment-6884
 * @see https://nextjs.org/docs/basic-features/data-fetching#getstaticprops-static-generation
 */
export const getStaticProps: GetStaticProps<SSGPageProps, CommonServerSideParams> = getPublicLayoutStaticProps;

/**
 * SSG pages are first rendered by the server (during static bundling)
 * Then, they're rendered by the client, and gain additional props (defined in OnlyBrowserPageProps)
 * Because this last case is the most common (server bundle only happens during development stage), we consider it a default
 * To represent this behaviour, we use the native Partial TS keyword to make all OnlyBrowserPageProps optional
 *
 * Beware props in OnlyBrowserPageProps are not available on the server
 */
type Props = {} & SSGPageProps<Partial<OnlyBrowserPageProps>>;

/**
 * Displays the Student Loan Simulator, AKA "SLS" page.
 *
 * SLS is an external react application built with "Create React App".
 *
 * SLS relies on a special global object "SIMULATOR_SETTINGS" that must be available under "window" top-level object.
 * It is important to first define "window.SIMULATOR_SETTINGS" and only then load the SLS JS script.
 * SLS CSS styles can be loaded in any order (but better load it before the JS triggers).
 *
 * @see https://github.com/UnlyEd/SLS
 */
const SimulatorPage: NextPage<Props> = (props): JSX.Element => {
  const { t } = useTranslation();
  const { lang }: I18n = useI18n();
  const theme = useTheme();
  const { amplitudeInstance } = useAmplitude();
  const customer: Customer = useCustomer();
  const { modSLS, modTFP } = customer;
  const {
    serviceLabel,
    slsReleaseName: release,
  } = modTFP || {};
  const allStudentSolutions: AirtableRecord<StudentSolution>[] = customer?.studentSolutions;
  const allEducationalPrograms: AirtableRecord<EducationalProgram>[] = customer?.educationalPrograms;
  const allCampuses: AirtableRecord<Campus>[] = customer?.campuses;

  // XXX SLS is only rendered on the browser, (no SSR) because it's a create-react-app
  if (isBrowser()) {
    // Inject simulator settings and then inject script
    window['SIMULATOR_SETTINGS'] = resolveSimulatorSettings(
      t,
      customer,
      modSLS,
      allStudentSolutions,
      allCampuses,
      allEducationalPrograms,
      lang,
      release,
      theme,
    );

    if (process.env.NEXT_PUBLIC_APP_STAGE !== 'production') {
      // eslint-disable-next-line no-console
      console.log('window.SIMULATOR_SETTINGS', window['SIMULATOR_SETTINGS']);
    }

    const script = document.createElement('script');
    script.src = `https://storage.googleapis.com/studylink-simulator/builds/student-loan-simulator-v${release}.js`;
    script.id = 'student-loan-simulator-js';

    document.body.appendChild(script);
  }

  return (
    <DefaultLayout
      {...props}
      pageName={AMPLITUDE_PAGES.SIMULATOR_PAGE}
      headProps={{
        seoTitle: serviceLabel,
        additionalContent: (
          <Fragment>
            <link
              id="student-loan-simulator-css"
              rel="stylesheet"
              href={`https://storage.googleapis.com/studylink-simulator/builds/student-loan-simulator-v${release}.css`}
            />
          </Fragment>
        ),
      }}
    >
      <Amplitude>
        {
          (props): JSX.Element => {
            const { logEvent } = props;
            if (isBrowser()) {
              // XXX This function will be called by SLS when "onFeedbackChange" event is triggered
              window['onSLSFeedbackChange'] = (props: { rating?: number; description?: string; feedbackId: string; eventType: string }): void => {
                const {
                  rating: grade,
                  description: comment,
                  feedbackId,
                  eventType,
                } = props;
                logger.debug(`"onSLSFeedbackChange" event triggered`);

                logEvent('SLS-user-feedback', {
                  feedback: {
                    grade,
                    comment,
                    feedbackId,
                  },
                  action: eventType,
                });

                // DA Enhance user properties
                const visitor: Identify = new amplitudeInstance.Identify();
                // XXX See https://github.com/amplitude/Amplitude-JavaScript/issues/223
                visitor.setOnce('initial_SLS_feedback_grade', grade);
                visitor.setOnce('initial_SLS_feedback_comment', comment);
                visitor.setOnce('initial_SLS_feedback_feedbackId', feedbackId);

                visitor.set('SLS_feedback_grade', grade);
                visitor.set('SLS_feedback_comment', comment);
                visitor.set('SLS_feedback_feedbackId', feedbackId);

                amplitudeInstance.identify(visitor);
              };
            }

            return (
              <Fragment>
                <div
                  // Overrides native's SLS styles
                  css={css`
                    .simulator-results-feedback-in-between-bloc-heading {
                      font-size: 1.5rem;
                    }
                  `}
                >
                  {/* XXX Simulator gets injected in this div when component loads */}
                  <div id="student-loan-simulator">
                    <Loader />
                  </div>
                </div>
              </Fragment>
            );
          }
        }
      </Amplitude>
    </DefaultLayout>
  );
};

export default (SimulatorPage);
